import { createSelector } from 'reselect';

import { type KeyMap } from 'helpers/interface';
import type { CannedResponse } from 'interfaces/canned-responses';
import type { IStoreState } from 'interfaces/store/store-state';
import { getLoggedInAgentLogin } from 'store/entities/agents/selectors';
import { shouldRestrictAgentAccess } from 'store/features/session/selectors';
import {
  createHasFetchedSelector,
  createRequestFetchingSelector,
  type IWithRequestsState,
} from 'store/requests/selectors';
import { getSelectedGroupId } from 'store/views/settings/selectors';

import { filterCannedResponsesByAccess } from './access-control';
import { CannedResponseActionNames } from './actions';
import { type ICannedResponseAutotags, type ICannedResponsesState } from './interfaces';

const EMPTY_ARRAY = [];

export interface IWithCannedResponsesState {
  entities: {
    cannedResponses: ICannedResponsesState;
  };
}

export const getIsFetchingCannedResponses = (state: IWithRequestsState): boolean =>
  createRequestFetchingSelector([CannedResponseActionNames.FETCH_CANNED_RESPONSES])(state);

export function getCannedResponses(state: IWithCannedResponsesState): KeyMap<CannedResponse> {
  return state.entities.cannedResponses.byIds;
}

export const getCannedResponseById = (state: IWithCannedResponsesState, id: number): CannedResponse | undefined => {
  return getCannedResponses(state)[id];
};

/**
 * Returns canned responses by their IDs.
 * **AC-1520**: This selector provides unrestricted access - all agents can USE all canned responses.
 * Use this for USAGE contexts (chat interface, AI suggestions, autocomplete).
 * For MANAGEMENT contexts (settings pages), use getCannedResponsesForSelectedGroupWithAccessControl instead.
 */
export const getCannedResponsesByIds = (
  state: IWithCannedResponsesState,
  ids?: readonly number[],
): CannedResponse[] => {
  if (!ids) {
    return [];
  }

  return ids.map((id) => getCannedResponseById(state, id)).filter((cr): cr is CannedResponse => cr !== undefined);
};

export function getCannedResponse(state: IWithCannedResponsesState, id: number): CannedResponse | null {
  const canned = state.entities.cannedResponses.byIds?.[id];
  if (!canned) {
    return null;
  }

  return {
    ...canned,
    tags: canned.tags,
  };
}

export const getCannedResponsesForGroup = (state: IWithCannedResponsesState, groupId: string): CannedResponse[] => {
  const cannedResponses = getCannedResponses(state);
  const res = Object.values(cannedResponses).filter((cannedResponse) => groupId === cannedResponse.groupId);

  if (!res.length) {
    return EMPTY_ARRAY as CannedResponse[];
  }

  return res;
};

export const getCannedResponsesForSelectedGroup = createSelector(
  [getCannedResponses, getSelectedGroupId],
  (cannedResponses, groupId): CannedResponse[] => {
    const res = Object.values(cannedResponses).filter((cannedResponse) => groupId === cannedResponse.groupId);

    return res.length ? res : (EMPTY_ARRAY as CannedResponse[]);
  },
);

/**
 * Returns canned responses for the selected group, filtered by access restrictions.
 * Restricted agents can only see private responses they created.
 */
export const getCannedResponsesForSelectedGroupWithAccessControl = createSelector(
  [getCannedResponsesForSelectedGroup, shouldRestrictAgentAccess, getLoggedInAgentLogin],
  (responses, isRestricted, loggedAgentLogin): CannedResponse[] => {
    return filterCannedResponsesByAccess(responses, isRestricted, loggedAgentLogin);
  },
);

export function getCannedResponsesIdsByTags(state: IWithCannedResponsesState): KeyMap<string[]> {
  return state.entities.cannedResponses.idsByTags;
}

export const getHasCannedResponseSaved = (state: IStoreState): boolean =>
  createHasFetchedSelector([CannedResponseActionNames.SAVE_CANNED_RESPONSE])(state);

export const getCannedResponseAutotags = (state: IStoreState): KeyMap<ICannedResponseAutotags> =>
  state.entities.cannedResponses.autotags;

export const getIsCannedResponseConnectedWithAutotags = (state: IStoreState, cannedId: number): boolean => {
  return !!getCannedResponseAutotags(state)[cannedId];
};

export const getCannedResponseAutotagsById = (state: IStoreState, cannedId: number): string[] => {
  const cannedAutotags = state.entities.cannedResponses.autotags[cannedId];

  if (!cannedAutotags) {
    return EMPTY_ARRAY as string[];
  }

  return cannedAutotags.tags;
};

export const getIsFetchingCannedResponsesAutotags = (state: IWithRequestsState): boolean =>
  createRequestFetchingSelector([CannedResponseActionNames.FETCH_CANNED_RESPONSE_AUTOTAGS_BASE])(state);
